import { LOG_STATUSES, NOTIFICATION_BY_CODE } from '../../../data/parentCommunicationLogsModel'

/** SES template_name → preference code (aligned with backend seed / service). */
const TEMPLATE_TO_CODE = {
	'kandor-parent-essay-updates': 'essay_updates',
	'kandor-parent-deadline-alerts': 'deadline_alerts',
	'kandor-parent-application-milestones': 'application_milestones',
	'kandor-parent-test-scores': 'test_scores',
	'kandor-parent-general-updates': 'general_updates',
}

const parseGradeNumber = grade => {
	if (grade == null || grade === '') return null
	if (typeof grade === 'number' && Number.isFinite(grade) && grade > 0) {
		return grade
	}
	const match = String(grade).match(/(\d+)/)
	if (!match) return null
	const num = Number(match[1])
	return Number.isFinite(num) && num > 0 ? num : null
}

const formatGradeLabel = (grade, gradeNumber) => {
	if (grade == null || grade === '') {
		return gradeNumber != null ? `Grade ${gradeNumber}` : ''
	}
	const raw = String(grade).trim()
	if (/^grade\s/i.test(raw)) return raw
	if (/^\d+$/.test(raw)) return `Grade ${raw}`
	return raw
}

/**
 * Derive event-level status from recipient statuses.
 * Partial is display-only for mixed success + failure/skipped.
 */
export const deriveEventStatus = recipients => {
	const statuses = (recipients || [])
		.map(item => item?.status)
		.filter(Boolean)

	if (!statuses.length) return LOG_STATUSES.FAILED

	const unique = new Set(statuses)
	if (unique.size === 1) {
		const only = statuses[0]
		if (
			only === LOG_STATUSES.SENT ||
			only === LOG_STATUSES.FAILED ||
			only === LOG_STATUSES.SKIPPED
		) {
			return only
		}
		return only
	}

	const hasSent = unique.has(LOG_STATUSES.SENT)
	if (hasSent) return LOG_STATUSES.PARTIAL

	// failed + skipped (no sent) → failed
	if (unique.has(LOG_STATUSES.FAILED)) return LOG_STATUSES.FAILED

	return LOG_STATUSES.PARTIAL
}

const resolveNotification = (templateName, notificationByTemplate = {}) => {
	const template = String(templateName || '').trim()
	if (!template) {
		return { code: '', name: '—' }
	}

	const fromPrefs = notificationByTemplate[template]
	if (fromPrefs?.code) {
		return {
			code: fromPrefs.code,
			name: fromPrefs.name || fromPrefs.code,
		}
	}

	const code = TEMPLATE_TO_CODE[template]
	if (code) {
		const known = NOTIFICATION_BY_CODE[code]
		return known || { code, name: code }
	}

	return { code: template, name: template }
}

const resolveStudent = (apiItem, studentsById) => {
	const studentId = String(apiItem?.student_id || '').trim()
	const fromStore = studentId ? studentsById.get(studentId) : null

	const gradeNumber =
		fromStore != null && Number.isFinite(Number(fromStore.grade))
			? Number(fromStore.grade)
			: parseGradeNumber(apiItem?.grade)

	const grade =
		fromStore?.gradeLabel ||
		formatGradeLabel(apiItem?.grade, gradeNumber)

	return {
		id: studentId || fromStore?.id || '',
		name: fromStore?.name || apiItem?.student_name || 'Unknown',
		grade,
		gradeNumber,
		avatarKey: fromStore?.avatarKey || null,
		avatarSource: fromStore?.avatarSource || null,
		photoUrl: fromStore?.photoUrl || '',
	}
}

const resolveRecipient = (apiRecipient, contactsById) => {
	const contactId = String(apiRecipient?.contact_id || '').trim()
	const email = apiRecipient?.recipient_email || ''
	const fromStore = contactId ? contactsById.get(contactId) : null

	return {
		id: contactId || email,
		name: fromStore?.contact_name || email || '—',
		email,
		type: fromStore?.contact_type || '',
		status: apiRecipient?.status || '',
		error_reason: apiRecipient?.error_reason ?? null,
	}
}

/**
 * Map counselor-wide delivery log API rows to Logs UI events.
 *
 * @param {Array} apiLogs
 * @param {{ studentsById?: Map, contactsById?: Map, notificationByTemplate?: Object }} enrichment
 */
export const mapParentDeliveryLogs = (apiLogs, enrichment = {}) => {
	const studentsById = enrichment.studentsById || new Map()
	const contactsById = enrichment.contactsById || new Map()
	const notificationByTemplate = enrichment.notificationByTemplate || {}

	const list = Array.isArray(apiLogs) ? apiLogs : []

	return list.map(item => {
		const student = resolveStudent(item, studentsById)
		const recipients = (item?.recipients || []).map(recipient =>
			resolveRecipient(recipient, contactsById),
		)
		const notification = resolveNotification(
			item?.template_name,
			notificationByTemplate,
		)
		const createdAt = item?.created_at || ''

		return {
			id: `${student.id}|${item?.template_name || ''}|${createdAt}`,
			student,
			notification,
			status: deriveEventStatus(recipients),
			created_at: createdAt,
			recipients,
		}
	})
}

export default mapParentDeliveryLogs
